import { Suspense, useEffect } from "react";

// Motion
import { motion } from "framer-motion";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { theme } from "../redux/customise/customiseActions";

// Router
import { Route, Switch, useLocation } from "react-router-dom";

// Routes
import { Routes } from "./routes";

// Layouts
import VerticalLayout from "../layout/VerticalLayout";
import HorizontalLayout from "../layout/HorizontalLayout";
import FullLayout from "../layout/FullLayout";

// Components
import Analytics from "../view/main/dashboard/analytics";
import Error404 from "../view/pages/errors/404";

import PrivateRoute from "./PrivateRoute"; // importe seu PrivateRoute
import PublicRouteWhenAuthenticated from "./PublicRouteWhenAuthenticated";

const PAGE_TITLES = {
  "/": "Dashboard — AllNube NFe",
  "/main/dashboard/analytics": "Dashboard Analytics — AllNube NFe",
  "/main/dashboard/nfes": "Notas Fiscais — AllNube NFe",
  "/main/dashboard/nfes/summary": "Resumo de NFs — AllNube NFe",
  "/main/dashboard/nfes/import": "Importar NFs — AllNube NFe",
  "/apps/contact": "Contatos — AllNube NFe",
  "/apps/users": "Usuários — AllNube NFe",
  "/apps/branch": "Filiais — AllNube NFe",
  "/apps/supplier": "Fornecedores — AllNube NFe",
  "/apps/product": "Produtos — AllNube NFe",
  "/apps/product-filter": "Filtro de Produtos — AllNube NFe",
  "/pages/superadmin": "Painel Superadmin — AllNube NFe",
  "/pages/faq": "FAQ — AllNube NFe",
  "/pages/authentication/login": "Login — AllNube NFe",
};

function getTitleForPath(pathname) {
  // Tenta match exato primeiro, depois prefixo mais longo
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  const match = Object.keys(PAGE_TITLES)
    .filter((p) => p !== "/" && pathname.startsWith(p))
    .sort((a, b) => b.length - a.length)[0];
  return match ? PAGE_TITLES[match] : "AllNube NFe";
}

function TitleUpdater() {
  const location = useLocation();
  useEffect(() => {
    document.title = getTitleForPath(location.pathname);
  }, [location.pathname]);
  return null;
}

export default function Router() {
  // Redux
  const customise = useSelector((state) => state.customise);
  const dispatch = useDispatch();

  // Dark Mode
  let themeLocal;

  useEffect(() => {
    if (localStorage) {
      themeLocal = localStorage.getItem("theme");
    }

    if (themeLocal === "light" || themeLocal === "dark") {
      document.querySelector("body").classList.add(themeLocal);
      dispatch(theme(themeLocal));
    } else {
      document.querySelector("body").classList.add(customise.theme);
      dispatch(theme(customise.theme));
    }
  }, []);

  // RTL
  useEffect(() => {
    if (customise.direction === "ltr") {
      document.querySelector("html").setAttribute("dir", "ltr");
    } else if (customise.direction === "rtl") {
      document.querySelector("html").setAttribute("dir", "rtl");
    }
  }, []);

  // Default Layout
  const DefaultLayout = customise.layout; // FullLayout or VerticalLayout

  // All of the available layouts
  const Layouts = { VerticalLayout, HorizontalLayout, FullLayout };

  // Return Filtered Array of Routes & Paths
  const LayoutRoutesAndPaths = (layout) => {
    const LayoutRoutes = [];
    const LayoutPaths = [];
    if (Routes) {
      // Checks if Route layout or Default layout matches current layout
      Routes.filter(
        (route) =>
          route.layout === layout &&
          (LayoutRoutes.push(route), LayoutPaths.push(route.path))
      );
    }

    return { LayoutRoutes, LayoutPaths };
  };

  // Return Route to Render
  const ResolveRoutes = () => {
    return Object.keys(Layouts).map((layout, index) => {
      const { LayoutRoutes, LayoutPaths } = LayoutRoutesAndPaths(layout);

      // console.log(`Debug: Processando layout ${layout}`);
      // console.log(`Debug: Caminhos para ${layout}:`, LayoutPaths);
      // console.log(
      //   `Debug: Rotas para ${layout}:`,
      //   LayoutRoutes.map((r) => r.path)
      // );

      let LayoutTag;
      if (DefaultLayout === "HorizontalLayout") {
        if (layout === "VerticalLayout") {
          LayoutTag = Layouts["HorizontalLayout"];
        } else {
          LayoutTag = Layouts[layout];
        }
      } else {
        LayoutTag = Layouts[layout];
      }

      return (
        <Route path={LayoutPaths} key={index}>
          <LayoutTag>
            <Switch>
              {LayoutRoutes.map((route) => {
                const Component = route.component;

                let wrappedComponent;
                if (route.auth) {
                  // Se a rota requer autenticação (dashboard, etc.)
                  wrappedComponent = (
                    <PrivateRoute>
                      <Component />
                    </PrivateRoute>
                  );
                } else if (
                  route.layout === "FullLayout" &&
                  (route.path === "/pages/authentication/login" ||
                    route.path === "/pages/authentication/recover-password" ||
                    route.path === "/pages/authentication/register" ||
                    route.path === "/pages/authentication/reset-password")
                ) {
                  // Se for uma rota de autenticação e o usuário já estiver logado
                  wrappedComponent = (
                    <PublicRouteWhenAuthenticated>
                      <Component />
                    </PublicRouteWhenAuthenticated>
                  );
                } else {
                  // Para outras rotas não autenticadas (ex: landing page não logado)
                  wrappedComponent = <Component />;
                }

                return (
                  <Route
                    key={route.path}
                    path={route.path}
                    exact={route.exact === true}
                    render={(props) => (
                      <Suspense fallback={null}>
                        {route.layout === "FullLayout" ? (
                          wrappedComponent
                        ) : (
                          <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                              type: "spring",
                              duration: 0.5,
                              delay: 0.5,
                            }}
                          >
                            {wrappedComponent}
                          </motion.div>
                        )}
                      </Suspense>
                    )}
                  />
                );
              })}
            </Switch>
          </LayoutTag>
        </Route>
      );
    });
  };

  return (
    <>
      <TitleUpdater />
    <Switch>
      {ResolveRoutes()}

      {/* Home Page - Exemplo com proteção */}
      <Route
        exact
        path={"/"}
        render={() => {
          const content =
            DefaultLayout === "HorizontalLayout" ? (
              <Layouts.HorizontalLayout>
                <Analytics />
              </Layouts.HorizontalLayout>
            ) : (
              <Layouts.VerticalLayout>
                <Analytics />
              </Layouts.VerticalLayout>
            );

          return <PrivateRoute>{content}</PrivateRoute>;
        }}
      />

      {/* NotFound */}
      <Route path="*">
        <Error404 />
      </Route>
    </Switch>
    </>
  );
}
